<template>
  <div>
    <div class="mt-3 h-100 card">
      <div class="d-flex flex-between-center py-2 border-200 border-bottom card-header">
        <h6 class="mb-0">
          {{ setsensor.sname }} ({{ setsensor.dfkey }})
        </h6>
        <div class="d-flex align-items-center gap-2">
          <span class="text-warning fs-11 fw-semibold">{{ formattedRecvdt }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-info-circle"
            viewBox="0 0 16 16"
            style="cursor:pointer"
            title="상세보기"
            @click="openDetailDrawer"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
          </svg>

          <img
            v-if="isStrenge === true"
            class="mr-2"
            :src="strengthIcon"
            alt="통신"
            height="16"
          >

          <img
            v-if="isSensorSetting === true"
            class="mr-2"
            style="cursor:pointer"
            :src="setting"
            alt="설정"
            height="24"
            @click="settingSensor"
          >
        </div>
      </div>
      
      <div
        class="overflow-hidden card"
        style="min-width: 12rem"
      >
        <div class="bg-card bg-holder" />
        <div class="position-relative card-body">
          <div class="d-flex flex-between-center py-2 border-200">
            <h6
              v-if="alertSetView.alert === true"
              style="cursor:pointer"
              @click="settingSensor();"
            >
              알림설정<span class="ms-2 rounded-pill badge badge-subtle-warning">{{ alertSetView.value }}</span>
            </h6>
            <h6 v-if="alertWarningView === true">
              <img
                :src="warning"
                style="cursor:pointer"
                height="15px"
                @click="onWaring"
              >
            </h6>
          </div>

          <img
            v-if="displayType === 'image'"
            class="d-block mx-auto"
            :src="sensorImg"
            alt=""
            width="100"
            style="cursor:pointer"
            @click="openDetailDrawer"
          >
          <div
            v-else
            class="mb-2 font-sans-serif text-warning text-center display-4 fs-3 fw-normal"
          >
            {{ props.setsensor.value }} {{ props.setsensor.unit }}
          </div>
            
          <MonitorLineChart
            class="pt-4"
            :setsensor="setsensor"
            :setdevice="setdevice"
          />   
        </div>
      </div>
    </div>
  </div>

  <SensorConfigSettingModal
    v-if="SensorConfigShowModal"
    :setdevice="setdevice"
    :setsensor="setsensor"
    @close="SensorConfigShowModal = false"
    @save="handleConfigSave"
  />

  <SensorDetailDrawer
    :is-open="isDrawerOpen"
    :setsensor="setsensor"
    :setdevice="setdevice"
    @close="closeDetailDrawer"
  />

  <AlertModal ref="alertModalRef" />
  <ConfirmModal ref="confirmRef" />
</template>


<script setup lang="ts">

import moment from 'moment'
import type { Device, SensorData, EventListResult } from '@/types/commontypes'
import {ref, computed } from 'vue';
import AlertModal from "../components/AlertModal.vue";
import ConfirmModal from "../components/ConfirmModal.vue";
import MonitorLineChart from './MonitorLineChart.vue';
import SensorConfigSettingModal from '../components/SensorConfigSettingModal.vue'
import SensorDetailDrawer from '../components/SensorDetailDrawer.vue'
import { useThemeStore } from '../stores/themeStore'

import setting from '@/assets/images/setting.svg' 
import water00 from "@/assets/images/water00.png"
import water01 from "@/assets/images/water01.png"
import water02 from "@/assets/images/water02.png"
import water03 from "@/assets/images/water03.png"

import can00_white from "@/assets/images/can00.png"
import can01_white from "@/assets/images/can01.png"
import can02_white from "@/assets/images/can02.png"
import can03_white from "@/assets/images/can03.png"
import can04_white from "@/assets/images/can04.png"

import can00_dark from "@/assets/images/can00_dark.png"
import can01_dark from "@/assets/images/can01_dark.png"
import can02_dark from "@/assets/images/can02_dark.png"
import can03_dark from "@/assets/images/can03_dark.png"
import can04_dark from "@/assets/images/can04_dark.png"

import w00_white from "@/assets/images/w00_white.png"
import w01_white from "@/assets/images/w01_white.png"
import w02_white from "@/assets/images/w02_white.png"
import w03_white from "@/assets/images/w03_white.png"
import w04_white from "@/assets/images/w04_white.png"


import w00_dark from "@/assets/images/w00_dark.png"
import w01_dark from "@/assets/images/w01_dark.png"
import w02_dark from "@/assets/images/w02_dark.png"
import w03_dark from "@/assets/images/w03_dark.png"
import w04_dark from "@/assets/images/w04_dark.png"

import warning from "@/assets/images/warning.png"

const themeStore = useThemeStore()

const props = defineProps<{
    setsensor: SensorData;
    setdevice: Device;
}>();

const emit = defineEmits(['config-updated', 'device-updated'])

const alertModalRef = ref();
const confirmRef = ref();
const SensorConfigShowModal = ref(false)
const isDrawerOpen = ref(false)


const displayType = computed(() => {
    if(props.setdevice.dcode === '0013' || props.setdevice.dcode === '0009')
        return 'image';
    else
        return 'text';
})

// 날짜 시간 포맷팅: 2025-12-28 20:01
const formattedRecvdt = computed(() => {
    if (!props.setsensor.recvdt) return ''
    return moment(props.setsensor.recvdt).format('YYYY-MM-DD HH:mm')
})

/**
 * 온도, 습도, 근접센서 만 센서설정 가능
 */
const isSensorSetting = computed(() => {
    const settingList = ['5','6','19']
    const sindex = String(props.setsensor.sindex)
    if(settingList.includes(sindex))
        return true
    else
        return false;
})

const isStrenge = computed(() => {
    if(props.setsensor.strength === null || props.setsensor.strength === undefined || props.setsensor.strength < 0)
        return false

    return true
})

// strength 값에 따라 아이콘 이미지 선택
const strengthIcon = computed(() => {
    const strength = props.setsensor.strength

    if(strength === null || strength === undefined) {
        return ''
    }

    // 테마에 따라 다른 아이콘 세트 사용
    const iconMap: Record<number, string> = themeStore.isDark ? {
        0: w00_dark,
        1: w01_dark,
        2: w02_dark,
        3: w03_dark,
        4: w04_dark
    } : {
        0: w00_white,
        1: w01_white,
        2: w02_white,
        3: w03_white,
        4: w04_white
    }

    // strength 값이 0~4 범위를 벗어나면 기본값 사용
    return iconMap[strength] || (themeStore.isDark ? w00_dark : w00_white)
})


// 테마에 따라 자동으로 이미지 변경
const sensorImg = computed(() => {
    const sensorObj = props.setsensor;
    const deviceObj = props.setdevice;

    switch(deviceObj.dcode) {
        case '0013': {
            // 물탱크
            const imageMap: Record<string, string> = {
                "0": water00,
                "1": water01,
                "2": water02,
                "3": water03,
                "4": water03,
                null: water00
            }
            return imageMap[sensorObj.value]
        }

        case '0009': {
            // 사료통 - 테마에 따라 다른 이미지
            const imageMap: Record<string, string> = {
                "0": themeStore.isDark ? can00_dark : can00_white,
                "1": themeStore.isDark ? can01_dark : can01_white,
                "2": themeStore.isDark ? can02_dark : can02_white,
                "3": themeStore.isDark ? can03_dark : can03_white,
                "4": themeStore.isDark ? can04_dark : can04_white,
                null: themeStore.isDark ? can00_dark : can00_white
            }
            return imageMap[sensorObj.value]
        }

        default:
            return ''
    }
})

/**
 * 센서별 알림설정 정보를 보여줄지 여부
 */
const alertSetView = computed(() => {
    const configJSON = JSON.parse(props.setdevice.config);

    const sensorObj = props.setsensor;
    
    const retJSON = {alert:false, value:0}
    retJSON.alert = configJSON?.set_sensor?.[sensorObj.dfkey]?.alert || false


    retJSON.value = configJSON?.set_sensor?.[sensorObj.dfkey]?.guage || configJSON?.set_gauge || 0;
    return retJSON;
})

/**
 * 이벤트 발생시 발생 상태를 표시함
 */
const alertWarningView = computed(() => {
    const devObj = props.setdevice as Record<string, any>;
    const sensorObj = props.setsensor;
    
    const statusKey = `${String(sensorObj.dfkey)}_status`;
    const statusValue = devObj[statusKey];

    if(statusValue === 1) {
        const svid = `sv${sensorObj.sindex}`;
        return ['sv19', 'sv20'].includes(svid);
    }

    return false;
})

function formatTimeAgo(nowtime:number, pasttime:number) {
    // 두 시간의 차이 계산 (밀리초)
    const difference = nowtime - pasttime;

    // 차이를 초 단위로 변환
    const seconds = Math.floor(difference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(seconds / (3600 * 24));

    // 조건에 따라 결과 문자열 생성
    if (days > 0) {
        return `${days}일 전`;
    } else if (hours > 0) {
        return `${hours}시간 전`;
    } else if (minutes > 0) {
        return `${minutes}분 전`;
    } else {
        return '방금 전'; // 1분 이내일 경우
    }
}


function getStrAgoTime(strpasttime:string) {
	let nowtime = (new Date()).getTime();
	let pasttime = new Date(moment(strpasttime).format('YYYY-MM-DD HH:mm:ss')).getTime();
	return formatTimeAgo(nowtime, pasttime);
}

// deviceid 에 현재 발생 상태의 이벤트 정보를 조회하여 나타냄
async function onWaring() {
    const devObj = props.setdevice;
    const data = JSON.stringify( { 'operator': "getEventlist2", 'deviceid' : devObj.deviceid});
    
    // content-type을 설정하고 데이터 송신
    var xhr2 = new XMLHttpRequest();
    xhr2.open('POST', '/ajax/ajaxcall');
    xhr2.setRequestHeader('Content-type', "application/json");
    xhr2.send(data);

    // 데이터 수신이 완료되면 표시
    xhr2.addEventListener('load', async function(){
        var result = xhr2.responseText;
        const eventList : EventListResult[] = JSON.parse(result);
        if(!eventList || eventList.length <= 0) {
            alertModalRef.value.showAlertModal("경고", "발생한 알림이 없습니다.");
            return;
        }

        const alertobj = eventList.find(item => {
            return item.dfkey === props.setsensor.dfkey
        })
        if(!alertobj)
            throw new Error('이벤트가 없네')

        // const alertobj :EventListResult = eventList[0];
        
        const strfrom = moment( alertobj.startdt).format("YYYY-MM-DD HH:mm:00");
        const stragotome = getStrAgoTime(strfrom);
       
        const strmsg = `알림내용 : ${alertobj.msg}<br>알림일시 : ${strfrom} (${stragotome})<br><br>알림을 완료처리 하시겠습니까?`
        const ok = await confirmRef.value.showConfirmModal(
            "확인",
            strmsg
        );

        if(ok) {        
            const data = JSON.stringify({ 
                'operator': "updateEventSingle", 
                'myid' : alertobj.myid, 
                'dfkey' : alertobj.dfkey, 
                'deviceid' : devObj.deviceid });

            var xhr = new XMLHttpRequest();
            xhr.open('POST', '/ajax/ajaxcall');
            xhr.setRequestHeader('Content-type', "application/json");
            xhr.send(data);

            xhr.addEventListener('load', function(){
                const result = JSON.parse(xhr.responseText);
                if(result.err == 0) {
                    // sensorImg는 computed 속성이므로 자동으로 업데이트됨
                    // 추가 작업이 필요하다면 여기에 작성
                    emit('device-updated')
                } else {
                    console.log("failed! ", result.err);
                    const errmsg = `이벤트 처리가 실패 했습니다. ${result.err}`
                    alertModalRef.value.showAlertModal("경고", errmsg);
                }
            });
        }
    });
}

const settingSensor = () => {
    SensorConfigShowModal.value = true;
}

const handleConfigSave = () => {
    // 부모 컴포넌트에 설정이 업데이트되었음을 알림
    emit('config-updated')
}

const openDetailDrawer = () => {
    isDrawerOpen.value = true;
}

const closeDetailDrawer = () => {
    isDrawerOpen.value = false;
}


</script>


<style lang="">
    
</style>